import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Lock as LockIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { visitsService, CanEditResponse } from '@services/visitsService';

interface VisitLockBannerProps {
  visitId: string;
  onStatusChange?: (canEdit: boolean) => void;
}

const VisitLockBanner: React.FC<VisitLockBannerProps> = ({
  visitId,
  onStatusChange
}) => {
  const [canEditInfo, setCanEditInfo] = useState<CanEditResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeUntilLock, setTimeUntilLock] = useState<number | null>(null);

  useEffect(() => {
    checkEditability();
    
    // Verifica din nou la fiecare minut
    const interval = setInterval(checkEditability, 60000);
    
    return () => clearInterval(interval);
  }, [visitId]);

  useEffect(() => {
    if (canEditInfo?.timeUntilLock && canEditInfo.timeUntilLock > 0) {
      setTimeUntilLock(canEditInfo.timeUntilLock);
      
      // Actualizeaza countdown-ul la fiecare minut
      const countdownInterval = setInterval(() => {
        setTimeUntilLock(prev => {
          if (prev && prev > 0) {
            return prev - 1;
          }
          return 0;
        });
      }, 60000);
      
      return () => clearInterval(countdownInterval);
    }
  }, [canEditInfo?.timeUntilLock]);

  const checkEditability = async () => {
    try {
      setLoading(true);
      const response = await visitsService.canEditVisit(visitId);
      setCanEditInfo(response);
      onStatusChange?.(response.canEdit);
    } catch (error) {
      console.error('Error checking visit editability:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}min`;
  };

  const getProgressValue = (): number => {
    if (!canEditInfo?.timeUntilLock) return 0;
    const totalMinutes = 12 * 60; // 12 ore in minute
    return ((totalMinutes - canEditInfo.timeUntilLock) / totalMinutes) * 100;
  };

  if (loading) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Verificare status consultatie...</AlertTitle>
      </Alert>
    );
  }

  if (!canEditInfo) {
    return null;
  }

  // Consultatie in draft - complet editabila
  if (canEditInfo.canEdit && !canEditInfo.timeUntilLock) {
    return (
      <Alert 
        severity="success" 
        sx={{ mb: 2 }}
        icon={<CheckCircleIcon />}
      >
        <AlertTitle>Consultatie in draft</AlertTitle>
        <Typography variant="body2">
          Consultatia poate fi modificata complet. Semneaza-o pentru a o face vizibila pacientului.
        </Typography>
      </Alert>
    );
  }

  // Consultatie semnata cu countdown
  if (canEditInfo.canEdit && canEditInfo.timeUntilLock && canEditInfo.timeUntilLock > 0) {
    return (
      <Alert 
        severity="warning" 
        sx={{ mb: 2 }}
        icon={<ScheduleIcon />}
        action={
          <Tooltip title="Verifica din nou statusul">
            <IconButton
              color="inherit"
              size="small"
              onClick={checkEditability}
            >
              <AccessTimeIcon />
            </IconButton>
          </Tooltip>
        }
      >
        <AlertTitle>Consultatie semnata - Countdown activ</AlertTitle>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Consultatia se blocheaza in <strong>{formatTime(timeUntilLock || canEditInfo.timeUntilLock)}</strong>.
            Dupa blocare, vei putea adauga doar addendum-uri.
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              icon={<ScheduleIcon />}
              label={`${formatTime(timeUntilLock || canEditInfo.timeUntilLock)} ramas`}
              size="small"
              color="warning"
              variant="outlined"
            />
            <Chip
              icon={<LockIcon />}
              label="Se blocheaza automat"
              size="small"
              color="error"
              variant="outlined"
            />
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={getProgressValue()}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: timeUntilLock && timeUntilLock < 60 ? '#f44336' : '#ff9800'
              }
            }}
          />
        </Box>
      </Alert>
    );
  }

  // Consultatie blocata
  if (!canEditInfo.canEdit && canEditInfo.reason?.includes('blocata')) {
    return (
      <Alert 
        severity="error" 
        sx={{ mb: 2 }}
        icon={<LockIcon />}
      >
        <AlertTitle>Consultatie blocata</AlertTitle>
        <Typography variant="body2">
          Consultatia este blocata si nu poate fi modificata. Foloseste "Adauga addendum" pentru corectii sau completari.
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Chip
            icon={<LockIcon />}
            label="Blocata permanent"
            size="small"
            color="error"
            variant="filled"
          />
        </Box>
      </Alert>
    );
  }

  // Alte cazuri
  return (
    <Alert 
      severity="info" 
      sx={{ mb: 2 }}
      icon={<WarningIcon />}
    >
      <AlertTitle>Status consultatie</AlertTitle>
      <Typography variant="body2">
        {canEditInfo.reason || 'Status necunoscut'}
      </Typography>
    </Alert>
  );
};

export default VisitLockBanner;
